// App03TaskBoard.tsx
import React, { useEffect, useState } from "react";
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
} from "@hello-pangea/dnd";
import { Icon, IconButton, Persona, PersonaSize } from "@fluentui/react";
import { Callout, DirectionalHint } from "@fluentui/react";
import { IApp03Task, IApp03TaskUser } from "../../types";
import { getTaskPriorityString, TaskPriority, TaskPriorityColors, TaskPriorityTextColors } from "../../refData/WorkflowType";
import { IApp03WorkflowStage } from "../../types/App03WorkflowStage";
import { useWindowResize } from "../../hooks/useWindowResize";
import { getTaskTimeOptionLabel } from "../../types/App03Task";


export interface ReorderStagePayload {
    MovedStageID: number;
    OldIndex: number;
    NewIndex: number;
}

export interface ReorderTaskPayload {
    MovedTaskID: number;
    SourceStageID: number;
    DestinationStageID: number;
    OldIndex: number;
    NewIndex: number;
    PrevTaskID?: number;   // NEW
    NextTaskID?: number;   // NEW
}



interface IApp03TaskBoardProps {
    stages: IApp03WorkflowStage[];
    tasks: IApp03Task[];
    topOffset?: number;
    enableDrag?: boolean;
    onTaskClick?: (task: IApp03Task) => void;
    onTaskEdit?: (taskID: number) => void;
    onTaskShortEdit?: (taskID: number) => void;
    onTaskView?: (taskID: number) => void;
    onTaskDelete?: (task: IApp03Task) => void;
    onListsReorder?: (payload: ReorderStagePayload) => void;
    onTasksReorder?: (payload: ReorderTaskPayload) => void;
    onNewTaskClick?: (defaultStageID?: number) => void;
    onTaskPriorityChange?: (taskID: number, newPriorityID: number) => void;
}


// -----------------------------
// NEW: map stages + tasks into lists
// -----------------------------
type ListItem = {
    id: string;
    task: IApp03Task;
    title: string;
    taskRef: string;
    epicRef: string | null;
    epicTitle: string | null;
    priority: keyof typeof TaskPriority;
    priorityId: number | null;
    plannedTimeLabel: string;
    plannedTime: number;
    assignees: string[];
};

type List = {
    id: string;
    title: string;
    headerColor: string;
    items: ListItem[];
};

type Lists = List[];

const mapDataToLists = (stages: IApp03WorkflowStage[], tasks: IApp03Task[]): Lists =>
    stages
        .sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0))
        .map(stage => ({
            id: stage.ID.toString(),
            title: stage.Title,
            headerColor: stage.SystemStage !== "Backlog" ? "#08366A" : "#000000",
            items: tasks
                .filter(task => task.WorkflowStageID === stage.ID)
                .map(task => ({
                    id: task.ID.toString(),
                    task: task,
                    title: task.Title,
                    taskRef: task.Workflow?.RefColumnID === 1 ? task.TaskRef : task.ExternalRef,
                    epicRef: task.Epic?.EpicRef ?? null,
                    epicTitle: task.Epic?.Title ?? null,
                    priority: getTaskPriorityString(task.TaskPriorityID ?? 1) as keyof typeof TaskPriority,
                    priorityId: task.TaskPriorityID,
                    plannedTimeLabel: getTaskTimeOptionLabel(task.TaskTimeOption),
                    plannedTime: Number(task.PlannedTime) || 0,
                    assignees: task.App03TaskUsers?.map(u => u.User.Title) ?? []
                }))
        }));


// -----------------------------
// Component
// -----------------------------
const App03TaskBoard: React.FC<IApp03TaskBoardProps> = ({
    stages,
    tasks,
    topOffset = 32,     // default value
    enableDrag = true,
    onTaskClick,
    onTaskEdit,
    onTaskShortEdit,
    onTaskView,
    onTaskDelete,
    onListsReorder,
    onTasksReorder,
    onNewTaskClick,
    onTaskPriorityChange,
}) => {

    const [lists, setLists] = useState<Lists>(() => mapDataToLists(stages, tasks));
    const [priorityCalloutTask, setPriorityCalloutTask] = useState<ListItem | null>(null);
    const [priorityTarget, setPriorityTarget] = useState<HTMLElement | null>(null);

    // Update lists if props change
    useEffect(() => {
        //console.log('stages', stages);
        //console.log('tasks', tasks);
        setLists(mapDataToLists(stages, tasks));
    }, [stages, tasks]);


    const handleOnDragEnd = (result: DropResult) => {
        const { source, destination, type, draggableId } = result;
        if (!destination) return;

        if (!enableDrag) return;

        // -----------------------------
        // Moving whole lists
        // -----------------------------
        if (type === "LIST") {
            const stageId = draggableId.replace("stage-", "");
            const newLists = Array.from(lists);
            const [movedList] = newLists.splice(source.index, 1);
            newLists.splice(destination.index, 0, movedList);
            setLists(newLists);
            // console.log(
            //     `Moved list "${movedList.title}" ${movedList.id} from ${source.index} → ${destination.index}`
            // );

            onListsReorder?.({
                MovedStageID: Number(movedList.id),
                OldIndex: source.index,
                NewIndex: destination.index
            });

            return;
        }

        // -----------------------------
        // Moving items (tasks)
        // -----------------------------
        const sourceListId = source.droppableId.replace("stage-", "");
        const destListId = destination.droppableId.replace("stage-", "");
        const sourceList = lists.find((l) => l.id === sourceListId)!;
        const destList = lists.find((l) => l.id === destListId)!;

        const sourceItems = Array.from(sourceList.items);
        const [movedItem] = sourceItems.splice(source.index, 1);


        // --- helper to compute neighbors ---
        const computeNeighbors = (items: any[], insertIndex: number) => {
            const prev = insertIndex > 0 ? items[insertIndex - 1].id : 0;
            const next = insertIndex < items.length - 1 ? items[insertIndex + 1].id : 0;
            return { prev, next };
        };

        // Reorder inside same list
        if (sourceList.id === destList.id) {
            sourceItems.splice(destination.index, 0, movedItem);

            const updated = lists.map((l) =>
                l.id === sourceList.id ? { ...l, items: sourceItems } : l
            );
            setLists(updated);

            const { prev, next } = computeNeighbors(sourceItems, destination.index);

            // console.log(
            //     `Reordered "${movedItem.title} ${movedItem.id}" inside "${sourceList.title} ${sourceList.id}" from ${source.index} → ${destination.index}", prev=${prev}, next=${next}`
            // );

            onTasksReorder?.({
                MovedTaskID: Number(movedItem.id),
                SourceStageID: Number(sourceList.id),
                DestinationStageID: Number(destList.id),
                OldIndex: source.index,
                NewIndex: destination.index,
                PrevTaskID: prev,
                NextTaskID: next,
            });

        } else {
            // Move to different list
            const destItems = Array.from(destList.items);
            destItems.splice(destination.index, 0, movedItem);

            const updated = lists.map((l) => {
                if (l.id === sourceList.id) return { ...l, items: sourceItems };
                if (l.id === destList.id) return { ...l, items: destItems };
                return l;
            });
            setLists(updated);

            const { prev, next } = computeNeighbors(destItems, destination.index);

            // console.log(
            //     `Moved item "${movedItem.title} ${movedItem.id}" from ${sourceList.title} ${sourceList.id} [${source.index}] → ${destList.title} ${destList.id}[${destination.index}] prev=${prev}, next=${next}`
            // );

            onTasksReorder?.({
                MovedTaskID: Number(movedItem.id),
                SourceStageID: Number(sourceList.id),
                DestinationStageID: Number(destList.id),
                OldIndex: source.index,
                NewIndex: destination.index,
                PrevTaskID: prev,
                NextTaskID: next,
            });
        }


    };


    const { height: viewportHeight } = useWindowResize();

    //const viewportHeight = window.innerHeight;
    const boardHeight = viewportHeight - topOffset;

    const updateTaskPriorityLocal = (taskID: number, newPriorityID: number) => {
        setLists(prevLists =>
            prevLists.map(list => ({
                ...list,
                items: list.items.map(item => {
                    if (item.task.ID !== taskID) return item;

                    return {
                        ...item,
                        priorityId: newPriorityID,
                        priority: getTaskPriorityString(newPriorityID) as keyof typeof TaskPriority,
                        task: {
                            ...item.task,
                            TaskPriorityID: newPriorityID
                        }
                    };
                })
            }))
        );
    };


    return (
        <>
            <div
                style={{
                    display: "flex",
                    padding: 5,
                    paddingTop: 0,
                    overflowX: "auto",
                    overflowY: "auto",
                    height: boardHeight,
                    alignItems: "stretch",
                    scrollBehavior: "smooth",
                }}
            >
                <DragDropContext onDragEnd={handleOnDragEnd}>
                    <Droppable droppableId="lists" direction="horizontal" type="LIST">
                        {(provided) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{ display: "flex", gap: 16, alignItems: "stretch" }}
                            >
                                {lists.map((list, listIndex) => (
                                    <Draggable draggableId={`stage-${list.id}`} index={listIndex} key={`stage-${list.id}`} isDragDisabled={!enableDrag}>
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                style={{
                                                    ...provided.draggableProps.style,
                                                }}

                                            >
                                                {/* LIST COLUMN */}
                                                <div
                                                    style={{
                                                        background: "#f4f5f7",
                                                        padding: 0,
                                                        borderRadius: 6,
                                                        //minWidth: 250,
                                                        width: 285,
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        minHeight: "100%",
                                                    }}
                                                >
                                                    {/* Header (drag handle for list) */}
                                                    <div
                                                        {...provided.dragHandleProps}
                                                        style={{
                                                            //backgroundColor: 'transparent',
                                                            //textAlign: "center",
                                                            color: list.headerColor,
                                                            fontWeight: "bold",
                                                            padding: 8,
                                                            paddingLeft: 15,
                                                            borderTopLeftRadius: 6,
                                                            borderTopRightRadius: 6,
                                                            cursor: !enableDrag ? "default" : "grab",
                                                        }}
                                                    >
                                                        {list.title}
                                                    </div>

                                                    {/* ITEMS */}
                                                    <Droppable droppableId={`stage-${list.id}`} type="ITEM">
                                                        {(provided) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.droppableProps}
                                                                style={{
                                                                    padding: 8,
                                                                    flexGrow: 1,
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                }}
                                                            >
                                                                {list.items.map((item, index) => {
                                                                    const priorityBgColor = TaskPriorityColors[item.priorityId] ?? "#999999";
                                                                    const priorityTextColor = TaskPriorityTextColors[item.priorityId] ?? "#000000";

                                                                    return (
                                                                        <Draggable
                                                                            key={`task-${item.id}`}
                                                                            draggableId={`task-${item.id}`}
                                                                            index={index}
                                                                            isDragDisabled={!enableDrag}
                                                                        >
                                                                            {(provided, snapshot) => (
                                                                                // Card contents start
                                                                                <div
                                                                                    ref={provided.innerRef}
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                    style={{
                                                                                        userSelect: "none",
                                                                                        padding: 12,
                                                                                        marginBottom: 8,
                                                                                        borderRadius: 6,
                                                                                        background: snapshot.isDragging ? "#e0f7fa" : "#fff",
                                                                                        boxShadow: snapshot.isDragging
                                                                                            ? "0 4px 12px rgba(0,0,0,0.15)"
                                                                                            : "0 1px 3px rgba(0,0,0,0.1)",
                                                                                        display: "flex",
                                                                                        flexDirection: "column",
                                                                                        gap: 6,
                                                                                        ...provided.draggableProps.style,
                                                                                    }}
                                                                                >
                                                                                    {/* TITLE */}
                                                                                    <div
                                                                                        onClick={() => onTaskClick?.(item.task)}
                                                                                        style={{
                                                                                            fontWeight: 500,
                                                                                            fontSize: 14,
                                                                                            //cursor: "pointer",
                                                                                            wordWrap: "break-word",
                                                                                            overflowWrap: "anywhere",
                                                                                            whiteSpace: "normal",
                                                                                        }}
                                                                                    >
                                                                                        {item.title}
                                                                                    </div>

                                                                                    {/* SECOND ROW — TASK CODE + PRIORITY + ASSIGNEES */}
                                                                                    <div
                                                                                        style={{
                                                                                            display: "flex",
                                                                                            justifyContent: "space-between",
                                                                                            alignItems: "center",
                                                                                            marginTop: 2,
                                                                                        }}
                                                                                    >
                                                                                        {/* LEFT SIDE → Code + Priority */}
                                                                                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>

                                                                                            {/* Task Code */}
                                                                                            <div
                                                                                                onClick={(e) => {
                                                                                                    if (!enableDrag) {
                                                                                                        e.preventDefault();
                                                                                                        e.stopPropagation();
                                                                                                        return;
                                                                                                    }
                                                                                                    onTaskShortEdit?.(item.task.ID);
                                                                                                }}
                                                                                                style={{
                                                                                                    cursor: !enableDrag ? "default" : "pointer",
                                                                                                    background: "#eef2ff",
                                                                                                    color: "#4338ca",
                                                                                                    padding: "2px 6px",
                                                                                                    borderRadius: 4,
                                                                                                    fontSize: 11,
                                                                                                    fontWeight: 600,
                                                                                                }}
                                                                                                title={enableDrag ? "Click for quick stage change and archive" : ""}
                                                                                            >
                                                                                                {item.taskRef}
                                                                                            </div>

                                                                                            {/* Epic Ref */}
                                                                                            {item.epicRef && (
                                                                                                <div
                                                                                                    title={`Epic: ${item.epicTitle ?? ""}`}
                                                                                                    style={{
                                                                                                        background: "#e0f7fa",
                                                                                                        color: "#006064",
                                                                                                        padding: "2px 6px",
                                                                                                        borderRadius: 4,
                                                                                                        fontSize: 11,
                                                                                                        fontWeight: 600,
                                                                                                        cursor: "default"
                                                                                                    }}
                                                                                                >
                                                                                                    {item.epicRef}
                                                                                                </div>
                                                                                            )}

                                                                                            {/* Priority badge (dynamic) */}
                                                                                            <div
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    
                                                                                                    if (!enableDrag) return;

                                                                                                    setPriorityCalloutTask(item);
                                                                                                    setPriorityTarget(e.currentTarget as HTMLElement);
                                                                                                }}
                                                                                                style={{
                                                                                                    padding: "2px 6px",
                                                                                                    borderRadius: 4,
                                                                                                    fontSize: 11,
                                                                                                    fontWeight: 600,
                                                                                                    background: priorityBgColor,
                                                                                                    color: priorityTextColor,
                                                                                                    cursor: !enableDrag ? "default" : "pointer",
                                                                                                }}
                                                                                            >
                                                                                                {item.priority}
                                                                                            </div>

                                                                                        </div>

                                                                                        {/* RIGHT SIDE — Assignees */}
                                                                                        <div style={{ display: "flex", gap: 1 }}>
                                                                                            {(item.assignees ?? []).slice(0, 2).map((person, i) => (
                                                                                                <Persona
                                                                                                    key={i}
                                                                                                    text={person}
                                                                                                    size={PersonaSize.size24}
                                                                                                    hidePersonaDetails
                                                                                                    styles={{ root: { cursor: "default" } }}
                                                                                                    title={person} // <-- tooltip on hover
                                                                                                />
                                                                                            ))}

                                                                                            {/* If more than 2 → show +N */}
                                                                                            {(item.assignees?.length ?? 0) > 2 && (
                                                                                                <div
                                                                                                    title={item.assignees.slice(2).map((u) => u).join(", ")}  // <-- Tooltip with remaining names
                                                                                                    style={{
                                                                                                        width: 24,
                                                                                                        height: 24,
                                                                                                        borderRadius: "50%",
                                                                                                        background: "#ddd",
                                                                                                        display: "flex",
                                                                                                        alignItems: "center",
                                                                                                        justifyContent: "center",
                                                                                                        fontSize: 10,
                                                                                                        fontWeight: 600,
                                                                                                        color: "#333",
                                                                                                        cursor: "default"
                                                                                                    }}
                                                                                                >
                                                                                                    +{item.assignees.length - 2}
                                                                                                </div>
                                                                                            )}

                                                                                        </div>
                                                                                    </div>

                                                                                    {/* THIRD ROW — STORY POINTS + EDIT/DELETE */}
                                                                                    <div
                                                                                        style={{
                                                                                            display: "flex",
                                                                                            justifyContent: "space-between",
                                                                                            alignItems: "center",
                                                                                            marginTop: 4,
                                                                                        }}
                                                                                    >
                                                                                        {/* Planned Time (Story Points | Days | Hours) */}
                                                                                        <div style={{ fontSize: 12, color: "#444" }}>
                                                                                            {item.plannedTimeLabel === "" ? (
                                                                                                <span style={{ visibility: "hidden" }}>placeholder</span>
                                                                                            ) : item.plannedTimeLabel === "Not Required" ? (
                                                                                                <>
                                                                                                    Task Time: <span style={{ fontWeight: 'normal' }}>Not Required</span>
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    {item.plannedTimeLabel}:{" "}
                                                                                                    <span style={{ fontWeight: 600 }}>{item.plannedTime}</span>
                                                                                                </>
                                                                                            )}
                                                                                        </div>



                                                                                        {/* Action Icons */}
                                                                                        <div style={{ display: "flex", gap: 2 }}>
                                                                                            <IconButton
                                                                                                iconProps={{ iconName: "Edit" }}
                                                                                                title="Edit"
                                                                                                ariaLabel="Edit"
                                                                                                disabled={!enableDrag}
                                                                                                styles={{ root: { height: 28, width: 28 } }}
                                                                                                onClick={() => onTaskEdit?.(item.task.ID)}
                                                                                            />
                                                                                            <IconButton
                                                                                                iconProps={{ iconName: "View" }}
                                                                                                title="View"
                                                                                                ariaLabel="View"
                                                                                                styles={{ root: { height: 28, width: 28 } }}
                                                                                                onClick={() => onTaskView?.(item.task.ID)}
                                                                                            />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                                // Card contents end
                                                                            )}
                                                                        </Draggable>
                                                                    );
                                                                })}
                                                                {provided.placeholder}

                                                                {/* + Add new card button */}
                                                                <div
                                                                    onClick={() => {
                                                                        if (!enableDrag) return;
                                                                        onNewTaskClick?.(Number(list.id));
                                                                    }}
                                                                    style={{
                                                                        marginTop: 6,
                                                                        padding: "4px 0",
                                                                        textAlign: "left",
                                                                        fontWeight: 500,
                                                                        color: "#0052CC",
                                                                        cursor: !enableDrag ? "not-allowed" : "pointer",
                                                                        userSelect: "none",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 4,
                                                                        fontSize: 13,
                                                                        opacity: !enableDrag ? 0.5 : 1,
                                                                    }}
                                                                //onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
                                                                //onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
                                                                >
                                                                    <Icon iconName="Add" style={{ fontSize: 14 }} />
                                                                    <span>Add Task</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Droppable>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>

            {/* PRIORITY CALLOUT */}
            {priorityCalloutTask && priorityTarget && (
                <Callout
                    target={priorityTarget}
                    onDismiss={() => {
                        setPriorityCalloutTask(null);
                        setPriorityTarget(null);
                    }}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    isBeakVisible={false}
                    gapSpace={6}
                    setInitialFocus
                    styles={{ calloutMain: { padding: 0, minWidth: 180 } }}
                >
                    <div style={{ padding: "10px 12px" }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>
                            Change Priority
                        </div>

                        {(Object.keys(TaskPriority) as Array<keyof typeof TaskPriority>)
                            .filter(k => isNaN(Number(k))) // enum cleanup
                            .map((key) => {
                                const priorityId = TaskPriority[key];
                                const bg = TaskPriorityColors[priorityId];
                                const color = TaskPriorityTextColors[priorityId];
                                const isSelected = priorityCalloutTask.priorityId === priorityId;

                                return (
                                    <div
                                        key={key}
                                        onClick={() => {
                                            const taskID = priorityCalloutTask.task.ID;
                                            // Optimistic UI update
                                            updateTaskPriorityLocal(taskID, priorityId);

                                            // Keep callout state in sync with optimistic update.
                                            // This ensures the selected priority highlights correctly
                                            // and prevents stale data if the callout remains open
                                            // or is re-used before parent data refreshes.
                                            setPriorityCalloutTask(prev =>
                                                prev
                                                    ? {
                                                        ...prev,
                                                        priorityId: priorityId,
                                                        priority: getTaskPriorityString(priorityId) as keyof typeof TaskPriority
                                                    }
                                                    : prev
                                            );

                                            // Notify parent (API / persistence)
                                            onTaskPriorityChange?.(taskID, priorityId);

                                            // Close callout
                                            setPriorityCalloutTask(null);
                                            setPriorityTarget(null);
                                        }}

                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "6px 8px",
                                            borderRadius: 4,
                                            cursor: "pointer",
                                            background: isSelected ? "#f3f2f1" : "transparent",
                                            fontWeight: isSelected ? 600 : 500
                                        }}
                                        onMouseEnter={e => (e.currentTarget.style.background = "#f3f2f1")}
                                        onMouseLeave={e =>
                                            (e.currentTarget.style.background = isSelected ? "#f3f2f1" : "transparent")
                                        }
                                    >
                                        <div
                                            style={{
                                                padding: "2px 6px",
                                                borderRadius: 4,
                                                fontSize: 11,
                                                fontWeight: 600,
                                                background: bg,
                                                color: color,
                                                width: 53
                                            }}
                                        >
                                            {key}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </Callout>
            )}

        </>

    );
};

export default App03TaskBoard;
